import { useState, useEffect } from 'react';
import ClientInfoSection from '../../components/clientProfile/ClientInfoSection/ClientInfoSection';
import RiskProfileSection from '../../components/clientProfile/RiskProfileSection/RiskProfileSection';
import GoalsSection from '../../components/clientProfile/GoalsSection/GoalsSection';
import AssetsSection from '../../components/clientProfile/AssetsSection/AssetsSection';
import LiabilitiesSection from '../../components/clientProfile/LiabilitiesSection/LiabilitiesSection';
import PreferencesSection from '../../components/clientProfile/PreferencesSection/PreferencesSection';
import {
  getProfileData,
  saveClientInfo,
  saveRiskProfile,
  saveGoals,
  saveAssets,
  saveLiabilities,
  savePreferences,
  markFieldNa,
  removeFieldNa,
  addFieldNote,
  flagField,
  removeFieldFlag
} from '../../api/clientProfileDataApi';
import './ClientProfileTab.css';

const SECTIONS = [
  { key: 'clientInfo',    label: 'Client Info',    icon: '👤' },
  { key: 'risk',          label: 'Risk Profile',   icon: '📊' },
  { key: 'goals',         label: 'Goals',          icon: '🎯' },
  { key: 'assets',        label: 'Assets',         icon: '💰' },
  { key: 'liabilities',   label: 'Liabilities',    icon: '📋' },
  { key: 'preferences',   label: 'Preferences',    icon: '⚙️' },
];

export default function ClientProfileTab({ client, profileVersion }) {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('clientInfo');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (client?._id) {
      fetchProfileData();
    }
  }, [client?._id]);

  async function fetchProfileData() {
    try {
      setLoading(true);
      const data = await getProfileData(client._id);
      setProfileData(data);
    } catch (err) {
      setError('Could not load profile data.');
    } finally {
      setLoading(false);
    }
  }

  function showToast(message, isError) {
    setToast({ message, isError: isError || false });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSaveClientInfo(responses) {
    try {
      setSaving(true);
      const result = await saveClientInfo(client._id, responses);
      setProfileData(result.profileData || result);
      showToast('Client info saved.');
    } catch (err) {
      showToast('Failed to save.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveRisk(responses) {
    try {
      setSaving(true);
      const result = await saveRiskProfile(client._id, responses);
      setProfileData(result.profileData || result);
    } catch (err) {
      showToast('Failed to save risk profile.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveGoals(selectedGoals) {
    try {
      setSaving(true);
      const result = await saveGoals(client._id, selectedGoals);
      setProfileData(result.profileData || result);
      showToast('Goals saved.');
    } catch (err) {
      showToast('Failed to save.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAssets(selectedSubCats, fieldResponses) {
    try {
      setSaving(true);
      const result = await saveAssets(client._id, selectedSubCats, fieldResponses);
      setProfileData(result.profileData || result);
      showToast('Assets saved.');
    } catch (err) {
      showToast('Failed to save.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveLiabilities(selectedSubCats, fieldResponses) {
    try {
      setSaving(true);
      const result = await saveLiabilities(client._id, selectedSubCats, fieldResponses);
      setProfileData(result.profileData || result);
      showToast('Liabilities saved.');
    } catch (err) {
      showToast('Failed to save.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePreferences(selectedPreferences) {
    try {
      setSaving(true);
      const result = await savePreferences(client._id, selectedPreferences);
      setProfileData(result.profileData || result);
      showToast('Preferences saved.');
    } catch (err) {
      showToast('Failed to save.', true);
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkNa(fieldUid) {
    try {
      const result = await markFieldNa(client._id, fieldUid);
      setProfileData(result.profileData || result);
    } catch (err) {
      showToast('Failed to mark N/A.', true);
    }
  }

  async function handleRemoveNa(fieldUid) {
    try {
      const result = await removeFieldNa(client._id, fieldUid);
      setProfileData(result.profileData || result);
    } catch (err) {
      showToast('Failed to remove N/A.', true);
    }
  }

  async function handleAddNote(fieldUid, note) {
    try {
      const result = await addFieldNote(client._id, fieldUid, note);
      setProfileData(result.profileData || result);
      showToast('Note saved.');
    } catch (err) {
      showToast('Failed to save note.', true);
    }
  }

  async function handleFlag(fieldUid) {
    try {
      const result = await flagField(client._id, fieldUid);
      setProfileData(result.profileData || result);
    } catch (err) {
      showToast('Failed to flag field.', true);
    }
  }

  async function handleRemoveFlag(fieldUid) {
    try {
      const result = await removeFieldFlag(client._id, fieldUid);
      setProfileData(result.profileData || result);
    } catch (err) {
      showToast('Failed to remove flag.', true);
    }
  }

  // Compute completion percentages from profile data + version snapshot
  function getSectionPercent(sectionKey) {
    if (!profileData || !profileVersion) return 0;
    const responses = profileData.responses || {};
    const hidden = profileData.hiddenFieldUids || [];

    if (sectionKey === 'clientInfo') {
      const fields = profileVersion.client_profile_fields_snapshot || [];
      const required = fields.filter(f => f.required);
      if (required.length === 0) return 0;
      const filled = required.filter(f => {
        if (hidden.includes(f.uid)) return true;
        const v = responses[f.uid];
        return v !== undefined && v !== null && v.toString().trim() !== '';
      });
      return Math.round((filled.length / required.length) * 100);
    }

    if (sectionKey === 'risk') {
      const questions = profileVersion.risk_questions_snapshot || [];
      if (questions.length === 0) return 0;
      const answered = questions.filter(q => {
        const r = responses[q.uid];
        return r !== undefined && r !== null && r.toString().trim() !== '' && r.toString() !== '[]';
      });
      return Math.round((answered.length / questions.length) * 100);
    }

    if (sectionKey === 'goals') {
      return (profileData.selected_goals || []).length > 0 ? 100 : 0;
    }

    if (sectionKey === 'assets') {
      const subCats = profileData.selected_asset_sub_categories || {};
      return Object.keys(subCats).some(k => subCats[k].length > 0) ? 100 : 0;
    }

    if (sectionKey === 'liabilities') {
      const subCats = profileData.selected_liability_sub_categories || {};
      return Object.keys(subCats).some(k => subCats[k].length > 0) ? 100 : 0;
    }

    if (sectionKey === 'preferences') {
      return (profileData.selected_preferences || []).length > 0 ? 100 : 0;
    }

    return 0;
  }

  function getOverallPercent() {
    let total = 0;
    for (let i = 0; i < SECTIONS.length; i++) {
      total += getSectionPercent(SECTIONS[i].key);
    }
    return Math.round(total / SECTIONS.length);
  }

  if (!profileVersion) {
    return (
      <div className="cpt__no-version">
        <p>No active profile version found. Please publish a profile version first.</p>
      </div>
    );
  }

  if (loading) return <div className="loading-state">Loading profile...</div>;
  if (error) return <div className="error-state">{error}</div>;

  const overallPercent = getOverallPercent();

  return (
    <div className="cpt">
      {/* Sidebar */}
      <div className="cpt__sidebar">
        <div className="cpt__sidebar-header">
          <span className="cpt__sidebar-title">Profile Sections</span>
        </div>

        <div className="cpt__section-list">
          {SECTIONS.map(section => {
            const percent = getSectionPercent(section.key);
            const isDone = percent === 100;
            return (
              <button
                key={section.key}
                className={`cpt__section-btn ${activeSection === section.key ? 'cpt__section-btn--active' : ''}`}
                onClick={() => setActiveSection(section.key)}
              >
                <span className="cpt__section-icon">{section.icon}</span>
                <span className="cpt__section-label">{section.label}</span>
                {isDone ? (
                  <span className="cpt__section-done">Done ✓</span>
                ) : (
                  <span className="cpt__section-pct">{percent}%</span>
                )}
                {!isDone && (
                  <div className="cpt__section-bar-wrap">
                    <div className="cpt__section-bar" style={{ width: `${percent}%` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="cpt__overall">
          <div className="cpt__overall-row">
            <span className="cpt__overall-label">Overall Completeness</span>
            <span className="cpt__overall-pct">{overallPercent}%</span>
          </div>
          <div className="cpt__overall-bar-wrap">
            <div className="cpt__overall-bar" style={{ width: `${overallPercent}%` }} />
          </div>
          {profileData?.risk_band && (
            <div className="cpt__risk-band">
              <span className="cpt__risk-band-label">Risk Band:</span>
              <span className="cpt__risk-band-value">{profileData.risk_band}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main panel */}
      <div className="cpt__main">
        {activeSection === 'clientInfo' && (
          <ClientInfoSection
            fields={profileVersion.client_profile_fields_snapshot || []}
            profileData={profileData}
            onSave={handleSaveClientInfo}
            onMarkNa={handleMarkNa}
            onRemoveNa={handleRemoveNa}
            onAddNote={handleAddNote}
            onFlag={handleFlag}
            onRemoveFlag={handleRemoveFlag}
            saving={saving}
          />
        )}

        {activeSection === 'risk' && (
          <RiskProfileSection
            questions={profileVersion.risk_questions_snapshot || []}
            scoreRanges={profileVersion.risk_score_ranges_snapshot || []}
            profileData={profileData}
            onSave={handleSaveRisk}
            saving={saving}
          />
        )}

        {activeSection === 'goals' && (
          <GoalsSection
            goalTemplates={profileVersion.goal_templates_snapshot || []}
            profileData={profileData}
            onSave={handleSaveGoals}
            saving={saving}
          />
        )}

        {activeSection === 'assets' && (
          <AssetsSection
            categories={profileVersion.asset_categories_snapshot || []}
            profileData={profileData}
            onSave={handleSaveAssets}
            saving={saving}
          />
        )}

        {activeSection === 'liabilities' && (
          <LiabilitiesSection
            categories={profileVersion.liability_categories_snapshot || []}
            profileData={profileData}
            onSave={handleSaveLiabilities}
            saving={saving}
          />
        )}

        {activeSection === 'preferences' && (
          <PreferencesSection
            preferenceTemplates={profileVersion.preference_templates_snapshot || []}
            profileData={profileData}
            onSave={handleSavePreferences}
            saving={saving}
          />
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`cpt__toast ${toast.isError ? 'cpt__toast--error' : 'cpt__toast--success'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
